// ─── turnMerge — รวม/เรียง + reconcile optimistic turn (ใช้ร่วม ChatRenderer + Clean²) ──
//
// logic ครั้งเดียวสำหรับทุก view (เดิมเขียนซ้ำใน ChatRenderer · WhimerCleanView มีของตัวเองที่
// พัง — reorder/หาย/รอคิวค้าง 2026-06-10). นี่คือฉบับ proven จาก ChatRenderer.
//
// โมเดล: REST history window = ความจริงล่าสุดเรียงถูกตาม JSONL · ประกอบเป็น
//   [ olderConfirmed (turn เก่าที่ scroll โหลดมา ก่อน window) | window | locals (optimistic ท้าย) ]
// - olderConfirmed = non-pending ที่ไม่อยู่ใน window (by id) → คงไว้ ไม่ drop (กัน turn หาย)
// - locals = optimistic (local-*) ที่ server ยังไม่มี content → คงท้าย · ค้างเกิน grace = failed
// - 'queued'/'sending' status: failIfStuck แตะเฉพาะ 'sending' → queued ไม่ถูก auto-fail

export interface ReconcileTurn {
  id: string;
  role: string;
  content: string;
  // STABLE identity across a JSONL rewrite (`<entry.uuid>-<blockIdx>`, set by the backend
  // reader). `id` is byte-offset-derived and SHIFTS when Claude rewrites the transcript
  // (temp+rename), so the SAME turn read by the live WS tail (old bytes) and by a later REST
  // refetch (new bytes) carries two different ids — reconcile then treats them as two turns and
  // double-renders/misorders (BUG-024). Keying dedup on srcKey (uuid-stable) fixes it; `id` is
  // still used for in-read ordering. Optional: absent on cli/optimistic turns → falls back to id.
  srcKey?: string;
  status?: string;
}

// grace สั้นตอน refetch (refresh/reconnect) → message ที่ส่งไม่ถึงถูก mark failed ทันที
export const FAIL_GRACE_MS = 4000;

// pending = ยังไม่เป็น confirmed transcript turn · local-* (optimistic ฝั่ง client) /
// submit-* (server track ไว้ agent ยังไม่ consume — มี status เอง ห้าม age)
export const isPending = (id: string): boolean => id.startsWith('local-') || id.startsWith('submit-');

// GIST เดียวกับ backend committedOracle (spec/COMMITTED_CUT.md): เก็บเฉพาะ letter/number/
// combining-mark — ใช้เช็ค live-presence (ข้อความยังอยู่ในคิวเหนือ composer ไหม)
export function gist(s: string): string {
  return s.replace(/[^\p{L}\p{N}\p{M}]/gu, '');
}

/** byte offset จาก id แบบ `<sid>-b<byte>-<k>` (REST + WS ใช้ scheme เดียวกันแล้ว 2026-06-10)
 *  · anchor ท้าย id กัน uuid ที่มี `-b3..-` ปน · null = id คนละ scheme (db row/optimistic) */
export function byteKey(id: string): number | null {
  const m = id.match(/-b(\d+)-\d+$/);
  return m ? parseInt(m[1], 10) : null;
}

/** flip optimistic 'sending' → 'failed' เมื่อเกิน grace · เรียกกับ local-* เท่านั้น
 *  (ts = segment ที่ 2 ของ id = Date.now()) · 'queued'/อื่น ไม่แตะ */
export function failIfStuck<T extends ReconcileTurn>(t: T, now: number): T {
  if (t.status !== 'sending') return t;
  const ts = Number(t.id.split('-')[1]) || 0;
  return now - ts > FAIL_GRACE_MS ? ({ ...t, status: 'failed' } as T) : t;
}

/**
 * WS structured_turn (turn ใหม่ finalize ใน JSONL) → รวมเข้า list ปัจจุบัน
 * ลำดับเช็คสำคัญ (บั๊กเดิม 2026-06-10): ต้อง "replace optimistic ที่รอ" **ก่อน** เช็ค dup —
 * ถ้าเช็ค dup ก่อน ข้อความเนื้อซ้ำเดิม (ผู้ใช้ส่ง "เทส" 2 รอบ) จะชน dup กับรอบแรก → turn ใหม่
 * ถูกทิ้ง → optimistic รอบสองค้าง ⏳ ตลอด (แล้วโดน checker ลบ = ข้อความหาย)
 *   1. user turn + มี optimistic เนื้อเดียวกันรอ → replace in-place (badge หายทันที)
 *   2. id เดียวกันมีแล้ว → dup จริง (WS replay / REST window ซ้อน — id byte-keyed ตรงกันทุก
 *      source แล้ว) → ทิ้ง · id คนละ scheme (agent อื่น) → fallback เทียบ content แบบเดิม
 *   3. นอกนั้น → append ท้าย
 */
export function applyStructuredTurn<T extends ReconcileTurn>(prev: T[], turn: T): T[] {
  if (turn.role === 'user') {
    const i = prev.findIndex(
      (t) => isPending(t.id) && t.role === 'user' && t.content.trim() === turn.content.trim(),
    );
    if (i !== -1) {
      const next = prev.slice();
      next[i] = turn;
      return next;
    }
  }
  // srcKey = identity เสถียรข้าม rewrite (uuid-based) → ถ้าตรง = turn เดียวกันแน่นอน แม้ byte id
  // ต่าง (claude rewrite ทำ byte เลื่อน) · เช็คก่อน id กัน BUG-024 double-render
  if (turn.srcKey && prev.some((t) => t.srcKey === turn.srcKey)) return prev;
  if (prev.some((t) => t.id === turn.id)) return prev;
  // id ไม่ byte-keyed (เช่น db row id ของ cli chat) → id เทียบข้าม source ไม่ได้ → กัน replay
  // ด้วย content แบบเดิม (ยอม suppress เนื้อซ้ำ เพื่อไม่ให้เบิ้ล — พฤติกรรมเดิมของ chat กลุ่มนั้น)
  if (byteKey(turn.id) === null) {
    const dupConfirmed = prev.some(
      (t) => !isPending(t.id) && t.role === turn.role && t.content.trim() === turn.content.trim(),
    );
    if (dupConfirmed) return prev;
  }
  return [...prev, turn];
}

/**
 * refetch (refresh/focus/reconnect): server คืน window ล่าสุดของ confirmed turn · merge ให้
 *   (a) คง turn เก่าที่ lazy-load มา (อยู่ก่อน window),
 *   (b) แทนช่วงล่าสุดด้วย window สด (จับ turn ที่เพิ่มจากที่อื่น เช่น --resume),
 *   (c) drop optimistic ที่ confirm แล้ว · fail ตัวที่ค้างเกิน grace.
 */
export function reconcileLocalTurns<T extends ReconcileTurn>(prev: T[], windowTurns: T[], now: number): T[] {
  // refetch ว่าง = ชั่วคราว (network blip / JSONL ยังไม่ flush) → คงของเดิม แค่ age local optimistic
  if (windowTurns.length === 0) {
    return prev.map((t) => (t.id.startsWith('local-') ? failIfStuck(t, now) : t));
  }
  const windowIds = new Set(windowTurns.map((t) => t.id));
  // srcKey ของ window: identity เสถียรข้าม rewrite · ใช้จับ "turn เดียวกันแต่ byte id ต่าง" (เพราะ
  // claude rewrite ทำ byte เลื่อน) — turn จาก WS ที่ append ไว้ (id เก่า) คือ turn เดียวกับใน window
  // (id ใหม่) ถ้า srcKey ตรง → ต้องไม่ถูกนับเป็น extra ไม่งั้นเด้งไปอยู่ "older" บนสุด + ซ้ำ (BUG-024)
  const windowKeys = new Set(windowTurns.map((t) => t.srcKey).filter((k): k is string => !!k));
  // content ของ user message ที่ server เห็นแล้ว (JSONL + submit-* ที่ track) → ใช้ drop optimistic ซ้ำ
  const serverUserContent = new Set(windowTurns.filter((t) => t.role === 'user').map((t) => t.content.trim()));
  // confirmed turn ที่ไม่อยู่ใน window: ส่วนใหญ่ = history เก่า (อยู่ก่อน window) แต่ WS turn ที่
  // เพิ่งมาระหว่าง refetch = ใหม่กว่า window → ต้องอยู่หลัง · แยกด้วย byte offset (id byte-keyed
  // เทียบกันได้ตรงๆ แล้ว) · id ไม่มี byte (cli/เก่า) → ถือเป็นเก่า (พฤติกรรมเดิม)
  const wBytes = windowTurns.map((t) => byteKey(t.id)).filter((b): b is number => b !== null);
  const wLast = wBytes.length ? Math.max(...wBytes) : null;
  // กรอง extra ทั้งด้วย id และ srcKey — turn ที่ srcKey ตรงกับ window = ตัวเดียวกัน window แทนให้แล้ว
  const extras = prev.filter(
    (t) => !isPending(t.id) && !windowIds.has(t.id) && !(t.srcKey && windowKeys.has(t.srcKey)),
  );
  const newer = wLast === null ? [] : extras.filter((t) => {
    const b = byteKey(t.id);
    return b !== null && b > wLast;
  });
  const older = extras.filter((t) => !newer.includes(t));
  const locals = prev
    .filter((t) => t.id.startsWith('local-'))
    .filter((t) => !serverUserContent.has(t.content.trim()))
    .map((t) => failIfStuck(t, now));
  return [...older, ...windowTurns, ...newer, ...locals];
}
